import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { SetAssignedUserActionStep } from "@redotech/redo-flows-common/oms/actions";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getTeamOMSUsers } from "@redotech/redo-merchant-fulfillment-app/utils/get-team-oms-users";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useRef, useState } from "react";
import { PartialWithRequiredType } from ".";

function userToListItem(user: OMSUser): RedoListItem<OMSUser> {
  return { id: user._id, value: user, type: "text", text: user.name };
}

export const UserDropdownInput = memo(function UserDropdownInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetAssignedUserActionStep>;
  setStep: (step: PartialWithRequiredType<SetAssignedUserActionStep>) => void;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const teamUsers = getTeamOMSUsers(team);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const userOptions: RedoListItem<OMSUser>[] = useMemo(() => {
    if (!teamUsers || teamUsers.length === 0) {
      return [];
    }
    return teamUsers.map(userToListItem);
  }, [teamUsers]);

  const selectedRoundRobinUsers = useMemo(
    () =>
      userOptions.filter((u) =>
        (step.roundRobinUserIds as string[] | undefined)?.includes(u.value._id),
      ),
    [userOptions, step.roundRobinUserIds],
  );

  const toggleRoundRobin = useHandler((value: CheckboxValue) => {
    if (value === true) {
      setStep({ ...step, roundRobin: true, userId: undefined });
    } else {
      setStep({ ...step, roundRobin: false, roundRobinUserIds: undefined });
    }
  });

  const setRoundRobinUsers = useHandler((items: RedoListItem<OMSUser>[]) => {
    setStep({ ...step, roundRobinUserIds: items.map((i) => i.value._id) });
  });

  const setAssignedUser = useHandler(
    ({ item }: { item: RedoListItem<OMSUser> }) => {
      setStep({ ...step, userId: item.value._id });
    },
  );

  const roundRobinButtonText =
    selectedRoundRobinUsers.length > 0
      ? selectedRoundRobinUsers.map((u) => u.value.name).join(", ")
      : t("Select users");

  return (
    <Flex dir="column" gap="xs">
      <RedoCheckbox
        label={t("Round robin")}
        setValue={toggleRoundRobin}
        value={(step.roundRobin as boolean | undefined) ?? false}
      />
      {step.roundRobin ? (
        <>
          <RedoButton
            hierarchy="secondary"
            IconTrailing={ChevronDownIcon}
            onClick={() => setOpen((o) => !o)}
            ref={anchorRef}
            size="xs"
            text={roundRobinButtonText}
          />
          <RedoMultiselectDropdown<OMSUser>
            dropdownAnchor={anchorRef.current}
            dropdownOpen={open}
            items={userOptions}
            selectedItems={selectedRoundRobinUsers}
            setDropdownOpen={setOpen}
            setSelectedItems={setRoundRobinUsers}
          />
        </>
      ) : (
        <RedoSingleSelectDropdownInput
          fitToAnchor={false}
          flexGrow={false}
          options={userOptions}
          optionSelected={setAssignedUser}
          placeholder={t("Select user")}
          selectedItem={userOptions.find((u) => u.value._id === step.userId)}
          size="xs"
        />
      )}
    </Flex>
  );
});
